import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { salesChannelService } from '@services/salesChannelService';
import { SalesChannel } from '@/types/salesChannel';

export const SalesChannelListView: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<SalesChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const pageSize = 20;

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await salesChannelService.getAll({
        page: currentPage,
        pageSize,
        searchName: search.trim() || undefined,
      });

      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [currentPage]);

  const handleSearch = () => {
    setCurrentPage(1);
    loadItems();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleDelete = async (id: number) => {
    try {
      await salesChannelService.delete(id);
      setDeleteConfirm(null);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h1">Canal de Venda</h1>
          <p className="text-slate-600">
            Gerencie os canais de venda
          </p>
        </div>
        <PermissionGuard permission="sales_channels.write">
          <Button onClick={() => navigate('/settings/sales/sales-channels/new')} icon={<Plus size={20} />}>
            Novo Canal
          </Button>
        </PermissionGuard>
      </div>

      {/* Busca */}
      <Card>
        <CardContent>
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nome..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <Button onClick={handleSearch} icon={<Search size={18} />}>
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">ID</th>
                <th className="text-left">Nome do Canal</th>
                <th className="text-right">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><div className="h-4 bg-slate-200 rounded w-12 animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-slate-200 rounded w-48 animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-slate-200 rounded w-20 animate-pulse ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-slate-500 py-8">
                    Nenhum canal de venda encontrado
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.sales_channel_id}>
                    <TableCell className="font-mono text-sm">
                      {item.sales_channel_id}
                    </TableCell>
                    <TableCell className="font-medium">
                      {item.sales_channel_name}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard permission="sales_channels.write">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(`/settings/sales/sales-channels/${item.sales_channel_id}/edit`)}
                            icon={<Edit size={14} />}
                          >
                            Editar
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="sales_channels.write">
                          {deleteConfirm === item.sales_channel_id ? (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDelete(item.sales_channel_id)}
                              >
                                Confirmar
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setDeleteConfirm(null)}
                              >
                                Cancelar
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteConfirm(item.sales_channel_id)}
                              icon={<Trash2 size={14} />}
                            >
                              Excluir
                            </Button>
                          )}
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">
            Total: {totalItems} registro(s)
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Anterior
            </Button>
            <span className="text-sm text-slate-600">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
